import { EditOutlined, UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Flex,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Space,
  Table,
  Typography,
  Upload,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import * as XLSX from "xlsx";
import {
  SlowboatPassport,
  SlowboatPassportInput,
  useGetSlowboatPassportsQuery,
  useImportSlowboatPassportsMutation,
  useUpdateSlowboatPassportMutation,
  useWithdrawSlowboatPassportMutation,
} from "../../../api/admin/slowboat/slowboatAdminApi";
import { normalizeRow } from "./normalizePassportRow";

const { Title, Text } = Typography;

export default function SlowboatPassportsPage() {
  const [messageApi, contextHolder] = message.useMessage();
  const [editing, setEditing] = useState<SlowboatPassport>();
  const [form] = Form.useForm<SlowboatPassportInput>();
  const [editForm] = Form.useForm<Partial<SlowboatPassportInput>>();
  const { data, isFetching, refetch } = useGetSlowboatPassportsQuery();
  const [importPassports, { isLoading: isImporting }] = useImportSlowboatPassportsMutation();
  const [updatePassport, { isLoading: isUpdating }] = useUpdateSlowboatPassportMutation();
  const [withdrawPassport, { isLoading: isWithdrawing }] = useWithdrawSlowboatPassportMutation();
  const loading = isFetching || isImporting || isUpdating || isWithdrawing;

  async function addPassport(values: SlowboatPassportInput) {
    try {
      await importPassports({ records: [values], groupLabel: values.group_label }).unwrap();
      messageApi.success("Passport submitted for provider verification");
      form.resetFields();
      await refetch();
    } catch (error) {
      messageApi.error(error instanceof Error ? error.message : "Unable to add passport");
    }
  }

  async function importFile(file: File) {
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet).map(normalizeRow);
      if (rows.length === 0) {
        throw new Error("The spreadsheet contains no passport rows");
      }
      if (rows.length > 200) {
        throw new Error("Slowboat accepts at most 200 passports per import");
      }
      const result = await importPassports({
        records: rows,
        groupLabel: rows[0].group_label,
      }).unwrap();
      messageApi.success(
        `Imported ${result.created}; skipped ${result.skipped_duplicates ?? 0} duplicate(s)`,
      );
      await refetch();
    } catch (error) {
      messageApi.error(error instanceof Error ? error.message : "Unable to import passports");
    }
    return false;
  }

  async function saveEdit(values: Partial<SlowboatPassportInput>) {
    if (!editing) return;
    try {
      await updatePassport({ id: editing.id, changes: values }).unwrap();
      messageApi.success("Passport updated");
      setEditing(undefined);
      await refetch();
    } catch (error) {
      messageApi.error(error instanceof Error ? error.message : "Unable to update passport");
    }
  }

  async function withdraw(record: SlowboatPassport) {
    try {
      await withdrawPassport(record.id).unwrap();
      messageApi.success("Passport withdrawn");
      await refetch();
    } catch (error) {
      messageApi.error(error instanceof Error ? error.message : "Unable to withdraw passport");
    }
  }

  function startEdit(record: SlowboatPassport) {
    setEditing(record);
    editForm.setFieldsValue(record);
  }

  const columns: ColumnsType<SlowboatPassport> = [
    { title: "First name", dataIndex: "first_name" },
    { title: "Last name", dataIndex: "last_name" },
    { title: "Passport no.", dataIndex: "passport_no" },
    { title: "Country", dataIndex: "passport_country" },
    { title: "Expiry", dataIndex: "passport_expiry_date" },
    { title: "Group", dataIndex: "group_label" },
    { title: "Status", dataIndex: "status" },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => startEdit(record)}>Edit</Button>
          <Popconfirm
            title="Withdraw this unused passport?"
            onConfirm={() => withdraw(record)}
          >
            <Button danger>Withdraw</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const fields = (isImport: boolean) => (
    <>
      <Flex gap={12}>
        <Form.Item name="title" label="Title" style={{ flex: 1 }}>
          <Input />
        </Form.Item>
        <Form.Item name="first_name" label="First name" rules={[{ required: true }]} style={{ flex: 2 }}>
          <Input />
        </Form.Item>
        <Form.Item name="last_name" label="Last name" rules={[{ required: true }]} style={{ flex: 2 }}>
          <Input />
        </Form.Item>
      </Flex>
      <Flex gap={12}>
        <Form.Item name="passport_no" label="Passport no." rules={[{ required: true }]} style={{ flex: 1 }}>
          <Input />
        </Form.Item>
        <Form.Item name="passport_country" label="Country" rules={isImport ? [{ required: true, whitespace: true }] : undefined} style={{ flex: 1 }}>
          <Input placeholder="GB" />
        </Form.Item>
        <Form.Item name="group_label" label="Group" style={{ flex: 1 }}>
          <Input />
        </Form.Item>
      </Flex>
      <Flex gap={12}>
        <Form.Item name="date_of_birth" label="Date of birth" rules={isImport ? [{ required: true, whitespace: true }] : undefined} style={{ flex: 1 }}>
          <Input placeholder="YYYY-MM-DD" />
        </Form.Item>
        <Form.Item name="passport_expiry_date" label="Passport expiry" style={{ flex: 1 }}>
          <Input placeholder="YYYY-MM-DD" />
        </Form.Item>
      </Flex>
    </>
  );

  return (
    <Flex vertical gap={16}>
      {contextHolder}
      <Title level={2}>Slowboat Passport Pool</Title>
      <Text>
        Imported passports remain pending until the provider verifies them. Activations use this shared pool;
        customer KYC is not submitted inline.
      </Text>
      <Card title="Add passport">
        <Form form={form} layout="vertical" onFinish={addPassport}>
          {fields(true)}
          <Button type="primary" htmlType="submit" loading={loading}>Add passport</Button>
        </Form>
      </Card>
      <Card
        title="Passport pool"
        extra={(
          <Upload
            accept=".csv,.xlsx,.xls"
            showUploadList={false}
            beforeUpload={importFile}
          >
            <Button icon={<UploadOutlined />}>Import CSV/XLSX</Button>
          </Upload>
        )}
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data?.records ?? []}
          loading={loading}
          scroll={{ x: true }}
        />
      </Card>
      <Modal
        title="Edit passport"
        open={editing != null}
        onCancel={() => setEditing(undefined)}
        onOk={() => editForm.submit()}
        confirmLoading={loading}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={saveEdit} preserve={false}>
          {fields(false)}
        </Form>
      </Modal>
    </Flex>
  );
}
